import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

export default function WorkerContractForm({ venueId, member, contract, onSave, onClose }) {
  const [form, setForm] = useState({
    venue_id: venueId,
    user_email: member?.user_email || "",
    user_name: member?.user_name || "",
    contract_type: "indefinit",
    workday_type: "full_time",
    weekly_hours: 40,
    annual_hours: 1826,
    daily_hours: 8,
    collective_agreement: "Hostaleria",
    complementary_hours_allowed: false,
    complementary_hours_limit: 0,
    planned_schedule_template: "dilluns_divendres",
    start_date: "",
    end_date: "",
    active: true,
    notes: "",
    ...contract,
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    if (contract?.id) {
      await hosys.entities.WorkerContract.update(contract.id, form);
    } else {
      await hosys.entities.WorkerContract.create(form);
    }
    setSaving(false);
    onSave();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Contracte de {member?.user_name || form.user_email}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Tipus contracte</Label>
              <Select value={form.contract_type} onValueChange={v => set("contract_type", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="indefinit">Indefinit</SelectItem>
                  <SelectItem value="temporal">Temporal</SelectItem>
                  <SelectItem value="formacio">Formació</SelectItem>
                  <SelectItem value="obra_servei">Obra i servei</SelectItem>
                  <SelectItem value="interinitat">Interinitat</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Tipus jornada</Label>
              <Select value={form.workday_type} onValueChange={v => set("workday_type", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="full_time">Temps complet</SelectItem>
                  <SelectItem value="part_time">Temps parcial</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Hores/dia</Label>
              <Input type="number" value={form.daily_hours} onChange={e => set("daily_hours", parseFloat(e.target.value))} />
            </div>
            <div>
              <Label className="text-xs">Hores/setmana</Label>
              <Input type="number" value={form.weekly_hours} onChange={e => set("weekly_hours", parseFloat(e.target.value))} />
            </div>
            <div>
              <Label className="text-xs">Hores anuals</Label>
              <Input type="number" value={form.annual_hours} onChange={e => set("annual_hours", parseFloat(e.target.value))} />
            </div>
          </div>

          <div>
            <Label className="text-xs">Conveni col·lectiu</Label>
            <Input value={form.collective_agreement} onChange={e => set("collective_agreement", e.target.value)} placeholder="Ex: Hostaleria i Restauració" />
          </div>

          <div>
            <Label className="text-xs">Plantilla d'horari</Label>
            <Select value={form.planned_schedule_template} onValueChange={v => set("planned_schedule_template", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="dilluns_divendres">Dilluns a divendres</SelectItem>
                <SelectItem value="dilluns_dissabte">Dilluns a dissabte</SelectItem>
                <SelectItem value="caps_setmana">Caps de setmana</SelectItem>
                <SelectItem value="torns_rotatius">Torns rotatius</SelectItem>
                <SelectItem value="personalitzat">Personalitzat</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Data inici</Label>
              <Input type="date" value={form.start_date} onChange={e => set("start_date", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Data fi (si temporal)</Label>
              <Input type="date" value={form.end_date || ""} onChange={e => set("end_date", e.target.value)} />
            </div>
          </div>

          {form.workday_type === "part_time" && (
            <div className="p-3 bg-blue-50 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium">Permet hores complementàries</Label>
                <Switch checked={form.complementary_hours_allowed} onCheckedChange={v => set("complementary_hours_allowed", v)} />
              </div>
              {form.complementary_hours_allowed && (
                <div>
                  <Label className="text-xs">Límit hores complementàries/setmana</Label>
                  <Input type="number" value={form.complementary_hours_limit} onChange={e => set("complementary_hours_limit", parseFloat(e.target.value))} />
                </div>
              )}
            </div>
          )}

          <div>
            <Label className="text-xs">Notes</Label>
            <Input value={form.notes || ""} onChange={e => set("notes", e.target.value)} placeholder="Observacions" />
          </div>

          <div className="flex items-center justify-between">
            <Label className="text-xs font-medium">Contracte actiu</Label>
            <Switch checked={form.active} onCheckedChange={v => set("active", v)} />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel·lar</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#7B2D3F] hover:bg-[#6a2636] text-white">
            {saving ? "Desant..." : "Desar contracte"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}